import React, { useCallback, useMemo, useState } from "react";
import { Row } from "@tanstack/react-table";
import { DataTable } from "ui-components/data-table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { PlanningFreightCategory, PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { usePlanningFreightList } from "../hooks/usePlanningFreightList";
import {
  useDrawerTableContainerHeight,
} from "../hooks/useDrawerTableContainerHeight";
import {
  getOutboundPlanningShipmentColumns,
  getRowCanExpandFreight,
  OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningShipmentColumns";
import PlanningFreightListSummary from "./planningFreightListSummary";
import {
  getOutboundPlanningItemColumns,
  OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningItemColumns";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import { CreateAppointment } from "features/appointment/createAppointment";
import "../outboundPlanningList.scss";

const MAIN_ROW_HEIGHT = 44;
const ITEM_ROW_HEIGHT = 36;

interface PlanningFreightListPanelProps {
  category: PlanningFreightCategory;
  originTerminalNumber: number;
  destinationTerminalNumbers: number[];
  fetchId: string;
}

const PlanningFreightListPanel: React.FC<PlanningFreightListPanelProps> = ({
  category,
  originTerminalNumber,
  destinationTerminalNumbers,
  fetchId,
}) => {
  const { containerRef, tableHeight } = useDrawerTableContainerHeight(24);
  const { data, isLoading, isError, refetch } = usePlanningFreightList(
    category,
    originTerminalNumber,
    destinationTerminalNumbers,
    fetchId,
    true
  );
  const [appointmentProbillNumber, setAppointmentProbillNumber] = useState<number | null>(null);

  const shipmentColumnPrefs = useDataTableColumnPreferences({
    instanceKey: `outbound-planning-${category}-shipments-v2`,
    columnOptions: OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS,
    hiddenByDefault: OUTBOUND_PLANNING_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
  });
  const itemColumnPrefs = useDataTableColumnPreferences({
    instanceKey: `outbound-planning-${category}-items`,
    columnOptions: OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
    hiddenByDefault: OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
  });

  const onScheduleAppointment = useCallback((probillNumber: number) => {
    setAppointmentProbillNumber(probillNumber);
  }, []);

  const shipmentColumns = useMemo(
    () =>
      getOutboundPlanningShipmentColumns({
        category,
        originTerminalNumber,
        onScheduleAppointment,
      }),
    [category, originTerminalNumber, onScheduleAppointment]
  );

  const itemColumns = useMemo(() => getOutboundPlanningItemColumns(), []);

  const renderSubComponent = useCallback(
    (row: Row<PlanningFreightProbill>) => (
      <div className="outbound-planning-freight-list-panel__sub-table">
        <DataTable
          columns={itemColumns}
          data={row.original.items ?? []}
          {...itemColumnPrefs}
          showColumnSelector
          columnSelectorOptions={OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS}
          enableColumnResize
          enableColumnReorder
          enableRowSelection={false}
          responsive
          autoHeight={false}
          shrinkToContent
          rowHeight={ITEM_ROW_HEIGHT}
          noResultsText="No items for this probill"
        />
      </div>
    ),
    [itemColumns, itemColumnPrefs]
  );

  return (
    <div className="outbound-planning-freight-list-panel">
      <PlanningFreightListSummary
        rows={data}
        isLoading={isLoading}
        includeHandlingUnits
      />
      <div ref={containerRef} className="outbound-planning-freight-list-panel__table">
        {isError ? (
          <XGSErrorMessage>Error loading freight list. Please try again.</XGSErrorMessage>
        ) : isLoading ? (
          <div className="outbound-planning-freight-list-panel__loading">
            <Loading isLoading />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="outbound-planning-freight-list-panel__empty">No shipments found.</div>
        ) : (
          <DataTable
            columns={shipmentColumns}
            data={data}
            isLoading={false}
            {...shipmentColumnPrefs}
            showColumnSelector
            columnSelectorOptions={OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS}
            enableColumnResize
            enableColumnReorder
            enableRowSelection={false}
            responsive
            sorting
            enableMultiSort
            filters
            renderSubComponent={renderSubComponent}
            getRowCanExpand={getRowCanExpandFreight}
            stickyLeadingColumnId="expand"
            autoHeight={false}
            listHeightOverride={tableHeight ?? 400}
            rowHeight={MAIN_ROW_HEIGHT}
            stickyHeaders
            customHeaderRowClass="outbound-planning-shipment-table__header-row"
          />
        )}
      </div>
      {appointmentProbillNumber != null && (
        <CreateAppointment
          probillNumber={appointmentProbillNumber}
          onClose={() => setAppointmentProbillNumber(null)}
          onSuccess={() => {
            setAppointmentProbillNumber(null);
            refetch();
          }}
          stackAbovePageModals
        />
      )}
    </div>
  );
};

export default PlanningFreightListPanel;
